import {
  AdditiveBlending,
  BufferGeometry,
  ConeGeometry,
  DataTexture,
  Float32BufferAttribute,
  InstancedMesh,
  Mesh,
  type MeshStandardMaterial,
  Object3D,
  Points,
  PointsMaterial,
  RGBAFormat,
  type Scene,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { type MedalTimes, medalsFromBot } from '../game/medals';
import type { ScenePalette } from './environment';
import { buildMap } from './mapkit/buildMap';
import type { MapSpec } from './mapkit/types';
import { loadSnowMaterials } from './materials';
import { mergeStaticMeshes } from './meshMerge';
import { makeSpire } from './ramps';
import type { BuiltLevel } from './TestLevel';

/**
 * "The Fall Line" — the game's namesake made playable: one long, continuous
 * timed descent down an alpine mountainside, with a churning wall of snow surging
 * behind you. Built on the dormant `MapSpec`/`buildMap` framework (which
 * auto-derives the start/checkpoint/end zones and pairs with `routeFromSpec` for
 * a headless bot baseline), so it is a real timed *course*, not free-surf. The
 * grey framework geometry is re-skinned in packed snow + ice and wrapped in
 * cold-daylight decor: falling snow, the avalanche wall, distant peaks, pines and
 * a glowing slalom gate at every checkpoint. All procedural — no assets.
 *
 * Difficulty climbs stage by stage (T1 → T4) the classic surf way: ramps narrow
 * (1024 → 640u), faces steepen (52° → 59°), declines bite harder and the air gaps
 * between stages widen — see docs/reference/MAP_DESIGN.md for the tier ratios.
 */

export const FALL_LINE_ID = 'surf_fall_line';

/**
 * The course — a true fall line: eight increasingly steep, increasingly narrow
 * pitches plunging straight down the mountain, each separated by an air gap and
 * marked by a checkpoint → `start → 7 checkpoints → end`. Straight lanes are not
 * a limitation but the theme: the fall line *is* the steepest straight-down path,
 * and a dead-straight ever-steepening plunge is the purest expression of it. The
 * difficulty climb is the classic surf one — ramps narrow (1024 → 720u), faces
 * steepen (52° → 58°), declines bite harder (11° → 14°) and the gaps widen — see
 * docs/reference/MAP_DESIGN.md for the tier ratios. Kept to the straight surf-
 * prism lane family so the joins are flush and the auto-generated `routeFromSpec`
 * bot can surf the whole thing to a valid finish (verified in FallLine.test.ts);
 * curves displace that bot off the line, so all turning lives in the backdrop.
 */
export const FALL_LINE_SPEC: MapSpec = {
  id: FALL_LINE_ID,
  name: 'The Fall Line',
  tier: 4,
  // Owner directive: rails/pillars/skirts hurt frame rate for no gameplay value.
  decor: { rails: false, pillars: false, skirts: false },
  stages: [
    // 1 — The Cornice: a wide, forgiving drop-in to build entry speed (T1).
    {
      lane: { width: 1024, angleDeg: 52, declineDeg: 11, segments: [{ length: 5600 }] },
      gap: { distance: 480, drop: 380 },
    },
    // 2 — Powder Bowl: still wide and mellow, a long pure-speed pitch (T1→T2).
    {
      lane: { width: 960, angleDeg: 53, declineDeg: 11, segments: [{ length: 5200 }] },
      gap: { distance: 500, drop: 400 },
    },
    // 3 — The Traverse: narrower, a touch steeper, longer carve to hold pace (T2).
    {
      lane: { width: 896, angleDeg: 54, declineDeg: 12, segments: [{ length: 4600 }] },
      gap: { distance: 520, drop: 420 },
    },
    // 4 — The Couloir: a tight, steep slot between rock walls (T3).
    {
      lane: { width: 832, angleDeg: 55, declineDeg: 12, segments: [{ length: 4200 }] },
      gap: { distance: 560, drop: 460 },
    },
    // 5 — Seracs: a fast pitch threading the ice blocks (T3).
    {
      lane: { width: 784, angleDeg: 56, declineDeg: 12, segments: [{ length: 4000 }] },
      gap: { distance: 520, drop: 420 },
    },
    // 6 — The Chute: narrow and steep, a committing plunge (T3→T4).
    {
      lane: { width: 752, angleDeg: 57, declineDeg: 12, segments: [{ length: 3800 }] },
      gap: { distance: 520, drop: 420 },
    },
    // 7 — The Headwall: a razor pitch, steep and narrow, big gap to clear (T4).
    {
      lane: { width: 728, angleDeg: 58, declineDeg: 13, segments: [{ length: 3600 }] },
      gap: { distance: 520, drop: 420 },
    },
    // 8 — Final Pitch: the last narrow, steep lane onto the wide runout (T4).
    {
      lane: { width: 720, angleDeg: 58, declineDeg: 13, segments: [{ length: 3200 }] },
    },
  ],
  runout: { length: 3200, width: 2600 },
};

/** Deep-cold daylight: a flat white-out sky (cheap, no HDRI) with cool fog/sun. */
export const fallLinePalette: ScenePalette = {
  background: 0xc4d2e4, // overcast alpine white-out
  fogColor: 0xcdd9ea,
  fogDensity: 0.000028, // light — distant ramps must stay readable down the mountain
  sunColor: 0xeef4ff, // cold, near-white winter sun
  sunIntensity: 2.7,
  envIntensity: 1.0, // hemisphere fill (flat-bg path)
  ambientColor: 0xb8c6da,
};

/**
 * Headless bot baseline (ticks) from `routeFromSpec`, measured in FallLine.test.ts.
 * Re-measure and update if the course geometry changes materially.
 */
const BOT_BASELINE_TICKS = 2210;

/** Medal targets, derived from the bot baseline via the spec-0011 ratios. */
export const fallLineMedals: MedalTimes = medalsFromBot(BOT_BASELINE_TICKS);

/** A soft round additive sprite (procedural radial gradient) — node-safe (no canvas). */
function softDot(size = 32): DataTexture {
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const fall = Math.max(0, 1 - Math.sqrt(dx * dx + dy * dy));
      const a = Math.round(255 * fall * fall);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = a;
    }
  }
  const tex = new DataTexture(data, size, size, RGBAFormat);
  tex.needsUpdate = true;
  return tex;
}

/** Axis-aligned bounds of the course colliders (for sizing the snow + backdrop). */
function bounds(meshes: Mesh[]): { min: Vector3; max: Vector3; center: Vector3 } {
  const min = new Vector3(Infinity, Infinity, Infinity);
  const max = new Vector3(-Infinity, -Infinity, -Infinity);
  const v = new Vector3();
  for (const m of meshes) {
    m.geometry.computeBoundingBox();
    const bb = m.geometry.boundingBox;
    if (!bb) continue;
    m.updateMatrixWorld(true);
    for (const corner of [bb.min, bb.max]) {
      v.copy(corner).applyMatrix4(m.matrixWorld);
      min.min(v);
      max.max(v);
    }
  }
  return { min, max, center: min.clone().add(max).multiplyScalar(0.5) };
}

interface FallLineDecor {
  snow: Points;
  avalanche: InstancedMesh;
  avalancheBase: Float32Array;
  avalancheDummy: Object3D;
  /** Top of the descent the avalanche surges from, and how far it travels. */
  startY: number;
  travelY: number;
}

/**
 * The cold-weather set dressing, all non-collidable. Falling snow fills the
 * course volume; the avalanche wall surges down from above the start; distant
 * peaks + pines + checkpoint gates give the mountain its identity. Returns the
 * animated bits so the render loop can drive them via `BuiltLevel.update`.
 */
function addSetPieces(
  scene: Scene,
  ramp: MeshStandardMaterial,
  decoMat: MeshStandardMaterial,
  span: { min: Vector3; max: Vector3; center: Vector3 },
  gates: { center: Vector3; lookAt: Vector3 }[],
): FallLineDecor {
  const { min, max, center } = span;
  const width = Math.max(max.x - min.x, max.z - min.z) + 8000;
  const height = max.y - min.y;

  // Falling snow — a soft additive point cloud across the whole descent volume.
  const N = 2600;
  const pos = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    pos[i * 3] = center.x + (Math.random() - 0.5) * width;
    pos[i * 3 + 1] = min.y + Math.random() * (height + 3000);
    pos[i * 3 + 2] = center.z + (Math.random() - 0.5) * width;
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  const snow = new Points(
    g,
    new PointsMaterial({
      color: 0xf4f9ff,
      size: 26,
      map: softDot(),
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  snow.frustumCulled = false;
  scene.add(snow);

  // The avalanche wall — a churning bank of snow that surges down from above the
  // top of the course. Instanced rough spheres packed into a broad wall; animated
  // to roll + advance downhill and loop. Purely cosmetic (never a collider).
  const CHUNKS = 220;
  const wallW = max.x - min.x + 5000;
  const wallSpan = Math.max(max.z - min.z, 1) + 5000;
  const startY = max.y + 1400;
  const travelY = height + 3000;
  const avalanche = new InstancedMesh(new SphereGeometry(1, 7, 6), ramp.clone(), CHUNKS);
  const avalancheBase = new Float32Array(CHUNKS * 3);
  const dummy = new Object3D();
  for (let i = 0; i < CHUNKS; i++) {
    const bx = center.x + (Math.random() - 0.5) * wallW;
    const by = (Math.random() - 0.5) * 1500; // thickness of the tumbling front
    const bz = center.z + (Math.random() - 0.5) * wallSpan * 0.25 - wallSpan * 0.35;
    avalancheBase[i * 3] = bx;
    avalancheBase[i * 3 + 1] = by;
    avalancheBase[i * 3 + 2] = bz;
    dummy.position.set(bx, startY + by, bz);
    dummy.scale.setScalar(360 + Math.random() * 520);
    dummy.updateMatrix();
    avalanche.setMatrixAt(i, dummy.matrix);
  }
  const avMat = avalanche.material as MeshStandardMaterial;
  avMat.color.setHex(0xf0f6ff);
  avMat.roughness = 0.9;
  avalanche.frustumCulled = false;
  scene.add(avalanche);

  // Distant peaks — big snow cones ringing the course (backdrop, no collision).
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.3;
    const r = width * 0.7 + Math.random() * 4000;
    const h = 6000 + Math.random() * 8000;
    const peak = new Mesh(
      makeSpire({ baseRadius: 2600, topRadius: 200, height: h, sides: 6 }),
      ramp,
    );
    peak.position.set(center.x + Math.cos(a) * r, min.y - 1200, center.z + Math.sin(a) * r);
    peak.castShadow = false;
    peak.receiveShadow = false;
    scene.add(peak);
  }

  // Pine silhouettes — instanced dark cones scattered low around the descent.
  const PINES = 120;
  const pineMesh = new InstancedMesh(new ConeGeometry(1, 1, 6), decoMat, PINES);
  for (let i = 0; i < PINES; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = width * 0.32 + Math.random() * width * 0.4;
    const s = 220 + Math.random() * 260;
    dummy.position.set(
      center.x + Math.cos(a) * r,
      min.y - 600 + Math.random() * 1400,
      center.z + Math.sin(a) * r,
    );
    dummy.scale.set(s, s * 3.2, s);
    dummy.rotation.set(0, Math.random() * 6.28, 0);
    dummy.updateMatrix();
    pineMesh.setMatrixAt(i, dummy.matrix);
  }
  pineMesh.frustumCulled = false;
  scene.add(pineMesh);

  // Slalom gates — a bright ring framing each checkpoint, oriented down the line.
  for (const gate of gates) {
    const ring = new Mesh(new TorusGeometry(440, 34, 12, 32), decoMat.clone());
    const gm = ring.material as MeshStandardMaterial;
    gm.color.setHex(0xff5a3c); // warm gate red — reads against the white-out
    gm.emissive.setHex(0xff5a3c);
    gm.emissiveIntensity = 0.5;
    ring.position.copy(gate.center);
    ring.lookAt(gate.lookAt);
    ring.castShadow = false;
    scene.add(ring);
  }

  return { snow, avalanche, avalancheBase, avalancheDummy: dummy, startY, travelY };
}

export async function buildFallLine(scene: Scene): Promise<BuiltLevel> {
  const materials = await loadSnowMaterials();
  const built = buildMap(FALL_LINE_SPEC);

  // Re-skin the grey framework meshes into snow/ice by their role-named meshes,
  // then add to the scene. Lanes share one ramp material + platforms one platform
  // material so the merge collapses the course into a couple of draw calls.
  for (const mesh of [...built.colliders, ...built.details]) {
    mesh.material = mesh.name.startsWith('platform') ? materials.platform : materials.ramp;
    scene.add(mesh);
  }
  const colliders = mergeStaticMeshes(scene, built.colliders);

  // Checkpoint gate anchors: each checkpoint zone, looking toward the next stage
  // entry (or the runout for the last) so the ring frames the travel line.
  const checkpoints = built.zones.filter((z) => z.kind === 'checkpoint');
  const gates = checkpoints.map((z, i) => ({
    center: z.center.clone(),
    lookAt: built.stageEntries[i + 2]?.clone() ?? built.spawn.clone().setY(z.center.y),
  }));

  const span = bounds(colliders);
  const decor = addSetPieces(
    scene,
    materials.ramp as MeshStandardMaterial,
    materials.decor as MeshStandardMaterial,
    span,
    gates,
  );

  // Animate snow fall + the surging avalanche front (real-time, dt-scaled).
  let t = 0;
  const SNOW_FALL = 520; // u/s
  const AVALANCHE_SPEED = 900; // u/s the front travels down the mountain
  const posAttr = decor.snow.geometry.getAttribute('position') as Float32BufferAttribute;
  const update = (dt: number): void => {
    t += dt;
    // Falling snow: drift down, wrap back to the top of the volume.
    for (let i = 0; i < posAttr.count; i++) {
      let y = posAttr.getY(i) - SNOW_FALL * dt;
      if (y < span.min.y - 400) {
        y = span.max.y + 2600;
      }
      posAttr.setY(i, y);
    }
    posAttr.needsUpdate = true;
    decor.snow.position.x = Math.sin(t * 0.3) * 220; // gentle wind sway

    // Avalanche: surge the whole front downhill, loop, and tumble each chunk.
    const travelled = (t * AVALANCHE_SPEED) % decor.travelY;
    const d = decor.avalancheDummy;
    for (let i = 0; i < decor.avalanche.count; i++) {
      const bx = decor.avalancheBase[i * 3] ?? 0;
      const by = decor.avalancheBase[i * 3 + 1] ?? 0;
      const bz = decor.avalancheBase[i * 3 + 2] ?? 0;
      const churn = Math.sin(t * 2.0 + i) * 140;
      d.position.set(bx, decor.startY + by - travelled + churn, bz);
      d.rotation.set(t * 1.3 + i, t * 0.7 + i, 0);
      d.scale.setScalar(360 + ((i * 53) % 520));
      d.updateMatrix();
      decor.avalanche.setMatrixAt(i, d.matrix);
    }
    decor.avalanche.instanceMatrix.needsUpdate = true;
  };

  return {
    mapId: FALL_LINE_ID,
    colliders,
    spawn: built.spawn,
    spawnYaw: built.spawnYaw,
    zones: built.zones,
    killY: built.killY,
    update,
  };
}
